import React, { useEffect, useState, useMemo } from 'react';
import '../../ApplicationsDashboard/ApplicationsDashboard.css';
import './ApplicationReports.css';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../components/Sidebar/Sidebar';
import SeedsLogo from "../../../assets/images/SeedsLogo.png";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faBell,
    faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import UserProfileDropdown from '../../../components/UserProfile/UserProfile';
import CustomPagination from '../../../components/Pagination/CustomPagination';
import { useDispatch, useSelector } from 'react-redux';
import { getApplicationsReport } from '../../../redux/ReportsReducer/action';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { downloadReport, downloadReportByDateRange, resetFilters } from '../../../utils/reportUtils';
import DownloadReport from '../DownloadReport';
import FunnelIconSvg from '../../../components/SVG/FunnelIconSvg';
import FilterModal from '../FilterModal/FilterModal';
import { getApplicationStatusList } from '../../../redux/ApplicationReducer/action';
import { getOfficers } from '../../../redux/UserReducer/action';
import { isAuthorised } from '../../../utils/common';
import { REPORTS_COMPONENTS } from '../../../config/reports';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';

const ApplicationReports = () => {
    let tokens = JSON.parse(sessionStorage.getItem("tokens")) || null;
    let authToken = tokens?.token || null;

    const { applicationsReport, pagination, applicationStatusList, officerProfile, officers } = useSelector((store) => {
        return {
            applicationsReport: store.ReportsReducer.applicationsReport.data,
            pagination: store.ReportsReducer.applicationsReport.pagination,
            applicationStatusList: store.ApplicationReducer.applicationStatusList,
            officerProfile: store.UserReducer.officerProfile,
            officers: store.UserReducer.officers.officers || []
        }
    }, []);

    const leads = [];

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const storedFilters = JSON.parse(sessionStorage.getItem('applicationFilters')) || {};

    const [applicationFilters, setApplicationFilters] = useState({
        seeds_id: storedFilters?.seeds_id || "",
        application_id: storedFilters?.application_id || "",
        customer_name: storedFilters?.customer_name || "",
        customer_id: storedFilters?.customer_id || "",
        branch_name: storedFilters?.branch_name || "",
        staff_id: storedFilters?.staff_id || "",
        // Keys match REPORT_FILTERS.applications (config/reports.js) and the
        // cascading Parent/Application Stage pattern used on the Applications
        // and Rejected Applications dashboards. The thunk translates these into
        // this report's own param names (parent_stage_name/stage) at dispatch.
        current_application_bucket: storedFilters?.current_application_bucket || "",
        application_stage_name: storedFilters?.application_stage_name || "",
        sector: storedFilters?.sector || "",
        sub_sector: storedFilters?.sub_sector || "",
        created_from: storedFilters?.created_from || "",
        created_to: storedFilters?.created_to || "",
        global_search: storedFilters?.global_search || ""
    });

    const [currentPage, setCurrentPage] = useState(parseInt(sessionStorage.getItem('reportsCurrentPage')) || 1);
    const [limit, setLimit] = useState(parseInt(sessionStorage.getItem('reportsLimit')) || 10);
    const totalCount = parseInt(sessionStorage.getItem('reportsTotalCount')) || 0;
    const totalPages = Math.ceil(totalCount / limit);

    const [dateRange, setDateRange] = useState([
        applicationFilters?.created_from ? new Date(applicationFilters.created_from) : null,
        applicationFilters?.created_to ? new Date(applicationFilters.created_to) : null,
    ]);

    const [startDate, endDate] = dateRange;

    useEffect(() => {
        dispatch(getApplicationsReport(currentPage, limit, applicationFilters, null));

        sessionStorage.setItem('reportsCurrentPage', currentPage);
        sessionStorage.setItem('reportsLimit', limit);
        sessionStorage.setItem('applicationFilters', JSON.stringify(applicationFilters));
        // }, [dispatch, limit, currentPage, applicationFilters]);
    }, [dispatch, limit, currentPage]);

    useEffect(() => {
        return () => {
            sessionStorage.removeItem("applicationFilters");
            sessionStorage.removeItem("reportsCurrentPage");
            sessionStorage.removeItem("reportsLimit");
            sessionStorage.removeItem("reportsTotalPages");
            sessionStorage.removeItem("reportsTotalCount");
        };
    }, []);

    const officerOptions = useMemo(() => {
        if (!officers || !Array.isArray(officers)) {
            return { roOptions: [], bmOptions: [], bcmOptions: [] };
        }

        const roList = officers.filter(
            (off) => off.role_code === 'RO' || off.role_name === 'Relationship Officer'
        );
        const bmList = officers.filter(
            (off) => off.role_code === 'BM' || off.role_name === 'Branch Manager'
        );
        const bcmList = officers.filter(
            (off) => off.role_code === 'BCM' || off.role_name === 'Branch Credit Manager'
        );

        return {
            roOptions: roList.map((o) => ({
                label: `${o.first_name || ''} ${o.last_name || ''} (${o.employee_id || o.id})`.trim(),
                value: `${o.first_name || ''} ${o.last_name || ''}`.trim(),
            })),
            bmOptions: bmList.map((o) => ({
                label: `${o.first_name || ''} ${o.last_name || ''} (${o.employee_id || o.id})`.trim(),
                value: `${o.first_name || ''} ${o.last_name || ''}`.trim(),
            })),
            bcmOptions: bcmList.map((o) => ({
                label: `${o.first_name || ''} ${o.last_name || ''} (${o.employee_id || o.id})`.trim(),
                value: `${o.first_name || ''} ${o.last_name || ''}`.trim(),
            })),
        };
    }, [officers]);

    useEffect(() => {
        dispatch(getApplicationStatusList({
            is_active: true
        }));
        // Populates the Branch Details dropdown in the All Filters panel,
        // same construction as Rejected/Deleted Applications.
        dispatch(getOfficers(1, 1000, { is_active: true }));
    }, [dispatch]);

    // Branch Details options for the All Filters panel. This report's backend
    // matches branch_name with an exact "=" (ApplicationReports.js), so the
    // option value must be the branch_name itself, not the branch_code.
    const branchOptions = React.useMemo(() => {
        if (!officers || !Array.isArray(officers)) return [];

        const options = officers
            .filter(branch => branch?.branch_name)
            .map(branch => ({
                label: `${branch.branch_code} - ${branch.branch_name}`,
                value: branch.branch_name
            }));

        return options.filter(
            (item, index, self) =>
                index === self.findIndex(obj => obj.value === item.value)
        );
    }, [officers]);

    const masterStatusList = applicationStatusList || [];

    // Distinct Parent Stages - same construction as the Applications and
    // Rejected Applications dashboards.
    const parentStageOptions = React.useMemo(() => {
        return [
            ...new Set(
                masterStatusList?.map(item => item.parent_stage_name)
            )
        ]
            .map(stage => ({
                label: stage,
                value: stage
            }));
    }, [masterStatusList]);

    // Application Stages scoped to the selected Parent Stage
    const applicationStageOptions = React.useMemo(() => {
        // If no parent selected → show all application stages
        if (!applicationFilters.current_application_bucket) {
            return [
                ...new Set(
                    masterStatusList?.map(item => item.application_stage_name)
                )
            ]
                .map(stage => ({
                    label: stage,
                    value: stage
                }));
        }

        // If parent selected → show only related application stages
        return [
            ...new Set(
                masterStatusList
                    ?.filter(
                        item =>
                            item.parent_stage_name === applicationFilters.current_application_bucket
                    )
                    ?.map(item => item.application_stage_name)
            )
        ]
            .map(stage => ({
                label: stage,
                value: stage
            }));
    }, [masterStatusList, applicationFilters.current_application_bucket]);

    const handlePageChange = (newPage) => {
        // Ensure we're working with 1-based pagination
        const pageToSet = Math.max(1, Math.min(newPage, totalPages));
        setCurrentPage(pageToSet);
        sessionStorage.setItem('reportsCurrentPage', pageToSet.toString());
    };

    const formatDate = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleDateRangeChange = (update) => {
        setDateRange(update);

        const [start, end] = update;

        const updatedFilters = {
            ...applicationFilters,
            created_from: formatDate(start),
            created_to: formatDate(end),
        };

        setApplicationFilters(updatedFilters);
        sessionStorage.setItem("applicationFilters", JSON.stringify(updatedFilters));
    };

    const handleFilterChange = (e) => {
        let { name, value } = e.target;

        if (typeof value === 'string') {
            value = value.replace(/[\t\n\r]+/g, '').replace(/^\s+/, '');
        }

        let updatedFilters = {
            ...applicationFilters,
            [name]: value
        };

        setApplicationFilters(updatedFilters);
        handlePageChange(1);
        sessionStorage.setItem("applicationFilters", JSON.stringify(updatedFilters));
    }

    useEffect(() => {
        sessionStorage.setItem('reportsCurrentPage', currentPage);
        sessionStorage.setItem('reportsLimit', limit);
    }, [limit, currentPage]);

    console.log({ applicationStatusList });

    return (
        <>
            <div className="reportsDashboardContainer">
                <div className="reportsDashboard">
                    <Sidebar />

                    <div className="dashboardMainContent">
                        <div className="contentHeader">
                            <div className="userLogo">
                                <img src={SeedsLogo} alt="logo" className="SeedsLogo" />
                            </div>
                            <div className="headerRightPanel">
                                {/* <div className="headerSearchBar">
                                <input type="text" placeholder="Search" />
                                <FontAwesomeIcon icon={faSearch} />
                            </div>
                            <div className="headerNotifications">
                                <FontAwesomeIcon icon={faBell} />
                                <span className="notificationBadge">9+</span>
                            </div>
                            <div className="headerHelp">
                                <FontAwesomeIcon icon={faQuestionCircle} />
                            </div> */}
                                <UserProfileDropdown />
                            </div>
                        </div>

                        <div className="pageContentWrapper">
                            <div className="pageHeaderSection">
                                <Breadcrumb items={[
                                    { label: 'Admin' },
                                    { label: 'Reports', onClick: () => navigate('/reports') },
                                    { label: 'Applications' }
                                ]} />

                                <div className="pageActions">
                                    <h2>Applications</h2>
                                </div>
                            </div>

                            <div className="searchControlSection">
                                <div className="searchGroup">
                                    {isAuthorised(
                                        officerProfile,
                                        REPORTS_COMPONENTS['applications_search']
                                    ) && <div className="tableSearchBar">
                                            {/* <FontAwesomeIcon icon={faSearch} className="searchBarIcon" /> */}
                                            <input
                                                type="search"
                                                placeholder="Search"
                                                name="global_search"
                                                value={applicationFilters?.global_search}
                                                onChange={handleFilterChange}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        dispatch(getApplicationsReport(1, limit, applicationFilters, null));
                                                        handlePageChange(1);
                                                    }
                                                }}
                                                onInput={(e) => {
                                                    if (e.target.value === "") {
                                                        let updatedFilters = {
                                                            ...applicationFilters,
                                                            global_search: e.target.value
                                                        }
                                                        setApplicationFilters(updatedFilters)
                                                        dispatch(getApplicationsReport(1, limit, updatedFilters));
                                                        handlePageChange(1);
                                                    }
                                                }}
                                            />
                                        </div>}

                                    {isAuthorised(
                                        officerProfile,
                                        REPORTS_COMPONENTS['applications_search']
                                    ) && <button
                                        className="searchButton iconOnly"
                                        title="Search"
                                        onClick={() => {
                                            dispatch(getApplicationsReport(1, limit, applicationFilters, null));
                                        }}
                                    >
                                            <FontAwesomeIcon icon={faSearch} />
                                        </button>}

                                         <div className="searchActionButtons">
                                    {isAuthorised(
                                        officerProfile,
                                        REPORTS_COMPONENTS['applications_download']
                                    ) && <button
                                        className="downloadButton"
                                        onClick={() => {
                                            const {
                                                current_application_bucket,
                                                application_stage_name,
                                                ...downloadFilters
                                            } = applicationFilters;

                                            if (current_application_bucket) {
                                                downloadFilters.parent_stage_name = current_application_bucket;
                                            }
                                            if (application_stage_name) {
                                                downloadFilters.stage = application_stage_name;
                                            }

                                            downloadReport("applications", downloadFilters, setIsDownloading);
                                        }}
                                        disabled={isDownloading}
                                    >
                                            {isDownloading ? (
                                                <>
                                                    <span className="buttonSpinner"></span>
                                                    Downloading...
                                                </>
                                            ) : (
                                                <>
                                                    Download
                                                </>
                                            )}
                                        </button>}
                                    {isAuthorised(
                                        officerProfile,
                                        REPORTS_COMPONENTS['applications_filters']
                                    ) && <button
                                        className="filterButton"
                                        onClick={() => setShowFilterModal(true)}
                                    >
                                            <FunnelIconSvg fillColor={"black"} />
                                        </button>}
                                </div>
                                </div>

                               
                            </div>

                            {
                                showFilterModal && (
                                    <FilterModal
                                        reportTitle={"applications"}
                                        filters={applicationFilters}
                                        setFilters={setApplicationFilters}
                                        resetFilters={() => {
                                            let resettedFilters = resetFilters(applicationFilters);

                                            setApplicationFilters(resettedFilters);
                                            dispatch(getApplicationsReport(1, limit, resettedFilters, null));
                                            handlePageChange(1);
                                            setShowFilterModal(false);
                                            sessionStorage.setItem("applicationFilters", JSON.stringify(resettedFilters));
                                            return resettedFilters;
                                        }}
                                        handleApplyFilters={(finalFilters) => {
                                            handlePageChange(1);
                                            dispatch(getApplicationsReport(1, limit, finalFilters, null));
                                            setShowFilterModal(false);
                                        }}
                                        onClose={() => {
                                            setShowFilterModal(false)
                                        }}
                                        dynamicOptions={{
                                            current_application_bucket: parentStageOptions,
                                            application_stage_name: applicationStageOptions,
                                            branch_name: branchOptions,
                                        }}
                                    />
                                )
                            }

                            <div className="tableCard">
                                <main className="">
                                    <div className="reportsTableContainer">
                                        <table className="applications-table" style={{ overflowX: "auto" }}>
                                            <thead>
                                                <tr>
                                                    <th>Application Number</th>
                                                    <th>Branch</th>
                                                    <th>Application Created Date</th>
                                                    <th>Application Stage Name</th>
                                                    <th>Customer Name</th>
                                                    <th>Customer ID</th>
                                                    <th>Borrower Name</th>
                                                    <th>Borrower Mobile</th>
                                                    <th>Borrower Address</th>
                                                    <th>Co-Borrower Name</th>
                                                    <th>Co-Borrower Mobile</th>
                                                    <th>Co-Borrower Address</th>
                                                    <th>Borrower CB Score</th>
                                                    <th>Co-Borrower CB Score</th>
                                                    <th>Amount Applied</th>
                                                    <th>RO Assigned</th>
                                                    <th>Borrower Aadhaar Detail</th>
                                                    <th>Borrower PAN Detail</th>
                                                    <th>Co-Borrower Aadhaar Detail</th>
                                                    <th>Co-Borrower PAN Detail</th>
                                                    <th>Borrower Marital Status</th>
                                                    <th>Borrower Mother Name</th>
                                                    <th>Borrower Father Name</th>
                                                    <th>Borrower Spouse Name</th>
                                                    <th>Borrower Religion</th>
                                                    <th>Borrower Social Category</th>
                                                    <th>Borrower Alternative Phone Number</th>
                                                    <th>Borrower Education Qualification</th>
                                                    <th>Number Of Member in Household</th>
                                                    {/* <th>Adults (&gt;18)</th> */}
                                                    {/* <th>Kids (&lt;5)</th> */}
                                                    {/* <th>Children (5-18)</th> */}
                                                    <th>Borrower Account Holder Name</th>
                                                    <th>Borrower Bank Account Number</th>
                                                    <th>Borrower Bank Name</th>
                                                    <th>Borrower Bank IFSC</th>
                                                    <th>Co-Borrower Marital Status</th>
                                                    <th>Co-Borrower Mother Name</th>
                                                    <th>Co-Borrower Father Name</th>
                                                    <th>Co-Borrower Spouse Name</th>
                                                    <th>Co-Borrower Religion</th>
                                                    <th>Co-Borrower Social Category</th>
                                                    <th>Co-Borrower Alternative Phone Number</th>
                                                    <th>Co-Borrower Relationship with Borrower</th>
                                                    <th>Co-Borrower Alternative Mobile Number</th>
                                                    <th>Nominee Name</th>
                                                    <th>Nominee Relation with borrower</th>
                                                    <th>Nominee gender</th>
                                                    <th>Borrower Current Address</th>
                                                    {/* <th>Years at Current Residence</th> */}
                                                    {/* <th>Years at Current City</th> */}
                                                    <th>Current Residence Type</th>
                                                    {/* <th>Current Area Size</th> */}
                                                    {/* <th>Current No Of Rooms</th> */}
                                                    {/* <th>Current People Living At Residence</th> */}
                                                    <th>Current Residence Ownership</th>
                                                    <th>Current Residence Location</th>
                                                    <th>Borrower Permanent Address</th>
                                                    {/* <th>Years at Permanent Residence</th> */}
                                                    {/* <th>Years at Permanent City</th> */}
                                                    <th>Permanent Residence Type</th>
                                                    {/* <th>Permanent Area Size</th> */}
                                                    {/* <th>Permanent No Of Rooms</th> */}
                                                    {/* <th>Permanent People Living At Residence</th> */}
                                                    <th>Permanent Residence Ownership</th>
                                                    <th>Permanent Residence Location</th>
                                                    <th>Business Legal Form</th>
                                                    <th>Business Sector</th>
                                                    <th>Business Sub-Sector</th>
                                                    <th>Business Name</th>
                                                    <th>Business License Type</th>
                                                    <th>Business License Number</th>
                                                    {/* <th>Business GST Number</th> */}
                                                    {/* <th>Primary Decision Maker</th> */}
                                                    <th>Is Primary Decision Maker is a Woman</th>
                                                    <th>Business Shop & Establishment Number</th>
                                                    {/* <th>Udyam Registration Number</th> */}
                                                    {/* <th>Member Of Market Association</th> */}
                                                    <th>Business Permises Ownership Status</th>
                                                    <th>Business Premise Location</th>
                                                    <th>Area of Business Premise (Sq Ft)</th>
                                                    <th>Business years Of Establishment</th>
                                                    <th>Years at Current Premises</th>
                                                    <th>Number of Paid Employee</th>
                                                    <th>Number of Family Members Regularly Working</th>
                                                    {/* <th>Number of Days Spent by Borrower in a Week</th> */}
                                                    <th>Daily Hours of Business</th>
                                                    <th>Monthly Operating Days</th>
                                                    {/* <th>Business Relocation Risk</th> */}
                                                    {/* <th>House Keeping Premise</th> */}
                                                    {/* <th>Book Keeping Status</th> */}
                                                    {/* <th>Market Reputation</th> */}
                                                    {/* <th>Social Reputation</th> */}
                                                    <th>Business Address</th>
                                                    <th>Business Location</th>
                                                    {/* <th>FI Verification date</th> */}
                                                    <th>Lead Creation Date</th>
                                                    <th>RO Submission Date</th>
                                                    <th>BM Submission Date</th>
                                                    <th>BCM Submission Date</th>
                                                    <th>CB Check Date</th>
                                                    <th>TVR Date</th>
                                                    <th>TVR By</th>
                                                    <th>Sanction Date</th>
                                                    <th>Sanction By</th>
                                                    <th>Insurance Premium</th>
                                                    <th>E-Signed Creation Date</th>
                                                    <th>E-Signed Created By</th>
                                                    <th>E-Signed Completed Date</th>
                                                    <th>Processing Fee</th>
                                                    <th>Processing Fee Receiving Date</th>
                                                    <th>Last Updated Date</th>
                                                    <th>Last Updated By</th>
                                                </tr>
                                                {/* <tr className="filter-row">
                                        <td>
                                            <input
                                                type="text"
                                                name="application_id"
                                                value={applicationFilters?.application_id || ""}
                                                placeholder="Search"
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                name="branch_name"
                                                value={applicationFilters?.branch_name || ""}
                                                placeholder="Search"
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            />
                                        </td>
                                        <td>
                                            <DatePicker
                                                selectsRange
                                                startDate={startDate}
                                                endDate={endDate}
                                                onChange={handleDateRangeChange}
                                                isClearable
                                                placeholderText="Select date range"
                                                className="form-control form-control-sm date-picker-filter"
                                            />
                                        </td>
                                        <td></td>
                                        <td>
                                            <input
                                                type="text"
                                                name="customer_name"
                                                value={applicationFilters?.customer_name || ""}
                                                placeholder="Search"
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                name="customer_id"
                                                value={applicationFilters?.customer_id || ""}
                                                placeholder="Search"
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            />
                                        </td>
                                        <td></td>
                                        <td>
                                            <DatePicker
                                                selectsRange
                                                startDate={startDate}
                                                endDate={endDate}
                                                onChange={handleDateRangeChange}
                                                isClearable
                                                placeholderText="Select date range"
                                                className="form-control form-control-sm date-picker-filter"
                                            /> 
                                        </td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr> */}
                                            </thead>
                                            <tbody>
                                                {
                                                    applicationsReport?.length === 0 ?
                                                        <tr>
                                                            <td colSpan={10} className="no-data-cell">
                                                                No records found
                                                            </td>
                                                        </tr> :
                                                        applicationsReport?.map((application, index) => (
                                                            <tr key={index}>
                                                                <td>{application?.seeds_id || application?.application_id}</td>
                                                                <td>{application?.branch_name}</td>
                                                                <td>{application?.application_created_at?.split('T')?.[0]}</td>
                                                                <td>{application?.application_stage_name}</td>
                                                                <td>{application?.customer_name}</td>
                                                                <td>{application?.customer_id}</td>
                                                                <td>{application?.borrower_name}</td>
                                                                <td>{application?.borrower_phone_number}</td>
                                                                <td>{application?.borrower_address}</td>
                                                                <td>{application?.co_borrower_name}</td>
                                                                <td>{application?.co_borrower_phone_number}</td>
                                                                <td>{application?.co_borrower_address}</td>
                                                                <td>{application?.borrower_credit_score}</td>
                                                                <td>{application?.co_borrower_credit_score}</td>
                                                                <td>{application?.applied_amount}</td>
                                                                <td>{application?.relationship_officer_name}</td>
                                                                <td>{application?.borrower_aadhaar_number}</td>
                                                                <td>{application?.borrower_pan_number}</td>
                                                                <td>{application?.co_borrower_aadhaar_number}</td>
                                                                <td>{application?.co_borrower_pan_number}l</td>
                                                                <td>{application?.borrower_marital_status}</td>
                                                                <td>{application?.borrower_mother_name}</td>
                                                                <td>{application?.borrower_father_name}</td>
                                                                <td>{application?.borrower_spouse_name}</td>
                                                                <td>{application?.borrower_religion}</td>
                                                                <td>{application?.borrower_social_category}</td>
                                                                <td>{application?.borrower_alternative_mobile_number}</td>
                                                                <td>{application?.borrower_educational_qualification}</td>
                                                                <td>{application?.number_of_members_in_household}</td>
                                                                {/* <td>{application?.borrower_adults}</td> */}
                                                                {/* <td>{application?.borrower_kids}</td> */}
                                                                {/* <td>{application?.borrower_children}</td> */}
                                                                <td>{application?.account_holder_name}</td>
                                                                <td>{application?.bank_account_number}</td>
                                                                <td>{application?.bank_name}</td>
                                                                <td>{application?.ifsc_code}</td>
                                                                <td>{application?.co_borrower_marital_status}</td>
                                                                <td>{application?.co_borrower_mother_name}</td>
                                                                <td>{application?.co_borrower_father_name}</td>
                                                                <td>{application?.co_borrower_spouse_name}</td>
                                                                <td>{application?.co_borrower_religion}</td>
                                                                <td>{application?.co_borrower_social_category}</td>
                                                                <td>{application?.co_borrower_alternative_mobile_number}</td>
                                                                <td>{application?.co_borrower_relation_with_borrower}</td>
                                                                <td>{application?.co_borrower_alternative_mobile_number}</td>
                                                                <td>{application?.nominee_name}</td>
                                                                <td>{application?.nominee_relationship}</td>
                                                                <td>{application?.nominee_gender}</td>
                                                                <td>{application?.current_residence_address}</td>
                                                                {/* <td>{application?.years_at_current_residence}</td> */}
                                                                {/* <td>{application?.years_at_current_city}</td> */}
                                                                <td>{application?.current_residence_type}</td>
                                                                {/* <td>{application?.current_residence_area_size_sqft}</td> */}
                                                                {/* <td>{application?.current_residence_number_of_rooms}</td> */}
                                                                {/* <td>{application?.current_residence_people_living_at_residence}</td> */}
                                                                <td>{application?.current_residence_ownership}</td>
                                                                <td>{application?.current_residence_location}</td>
                                                                <td>{application?.permanent_residence_address}</td>
                                                                {/* <td>{application?.years_at_permanent_residence}</td> */}
                                                                {/* <td>{application?.years_at_permanent_city}</td> */}
                                                                <td>{application?.permanent_residence_type}</td>
                                                                {/* <td>{application?.permanent_residence_area_size_sqft}</td> */}
                                                                {/* <td>{application?.permanent_residence_number_of_rooms}</td> */}
                                                                {/* <td>{application?.permanent_residence_people_living_at_residence}</td> */}
                                                                <td>{application?.permanent_residence_ownership}</td>
                                                                <td>{application?.permanent_residence_location}</td>
                                                                <td>{application?.business_legal_form}</td>
                                                                <td>{application?.business_sector}</td>
                                                                <td>{application?.business_sub_sector}</td>
                                                                <td>{application?.business_name}</td>
                                                                <td>{application?.business_license_type}</td>
                                                                <td>{application?.license_number}</td>
                                                                {/* <td>{application?.gstin_number}</td> */}
                                                                {/* <td>{application?.primary_decision_maker}</td> */}
                                                                <td>{application?.is_primary_decision_maker_woman ? "YES" : "NO"}</td>
                                                                <td>{application?.shop_establishment_number}</td>
                                                                {/* <td>{application?.udhyam_registration_number}</td> */}
                                                                {/* <td>{application?.membership_of_market_association}</td> */}
                                                                <td>{application?.business_premises_ownership_status}</td>
                                                                <td>{application?.business_premise_location}</td>
                                                                <td>{application?.area_of_business_premise}</td>
                                                                <td>{application?.business_year_of_establishment}</td>
                                                                <td>{application?.years_at_current_premises}</td>
                                                                <td>{application?.number_of_paid_employees}</td>
                                                                <td>{application?.number_of_family_members_regularly_working}</td>
                                                                {/* <td>{application?.number_of_days_spent_by_borrower}</td> */}
                                                                <td>{application?.daily_hours_of_business}</td>
                                                                <td>{application?.monthly_operating_days}</td>
                                                                {/* <td>{application?.business_relocation_risk}</td> */}
                                                                {/* <td>{application?.house_keeping_premise}</td> */}
                                                                {/* <td>{application?.book_keeping_status}</td> */}
                                                                {/* <td>{application?.market_reputation}</td> */}
                                                                {/* <td>{application?.social_reputation}</td> */}
                                                                <td>{application?.business_address}</td>
                                                                <td>{application?.business_location}</td>
                                                                {/* <td>{application?.fi_verified_date}</td> */}
                                                                <td>{application?.lead_creation_date}</td>
                                                                <td>{application?.ro_submission_date}</td>
                                                                <td>{application?.bm_submission_date}</td>
                                                                <td>{application?.bcm_submission_date}</td>
                                                                <td>{application?.cb_check_date}</td>
                                                                <td>{application?.tvr_date?.split('T')?.[0]}</td>
                                                                <td>{application?.tvr_by}</td>
                                                                <td>{application?.sanction_date?.split('T')?.[0]}</td>
                                                                <td>{application?.sanction_by}</td>
                                                                <td>{application?.insurance_premium}</td>
                                                                <td>{application?.esign_created_date?.split('T')?.[0]}</td>
                                                                <td>{application?.esign_created_by}</td>
                                                                <td>{application?.esign_completed_date?.split('T')?.[0]}</td>
                                                                <td>{application?.processing_fee}</td>
                                                                <td>{application?.processing_fee_paid_date?.split('T')?.[0]}</td>
                                                                <td>{application?.last_updated_date?.split('T')?.[0]}</td>
                                                                <td>{application?.last_updated_by}</td>
                                                            </tr>
                                                        ))
                                                }
                                            </tbody>
                                        </table>
                                    </div>

                                    {applicationsReport?.length > 0 && <div className="paginationControls">
                                        <CustomPagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>}
                                </main>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {
                showDownloadModal && (
                    <DownloadReport
                        isOpen={showDownloadModal}
                        onClose={() => setShowDownloadModal(false)}
                        reportTitle={"applications"}
                    />
                )
            }
        </>
    )
}

export default ApplicationReports